import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';
import ChartSkeleton from '../../../components/ChartSkeleton';
import SectionRetry from '../../../components/SectionRetry';
import SkeletonBlock from '../../../components/SkeletonBlock';
import TableSkeleton from '../../../components/TableSkeleton';
import type { AsyncSectionState } from '../../../hooks/useAsyncSection';

// Per-section state: each section shows its own loading, failure and empty state; the others keep rendering. The static dashboard always passes ready.
export interface WesSectionStatus {
  state: 'ready' | 'loading' | 'error';
  message?: string;
  onRetry?: () => void;
}

export const WES_READY: WesSectionStatus = { state: 'ready' };

// No data yet counts as loading: a section with nothing to show must never render stale or placeholder values.
export const toSectionStatus = (section: AsyncSectionState<unknown>): WesSectionStatus =>
  section.error
    ? { state: 'error', message: section.error, onRetry: section.retry }
    : section.loading || section.data === null
      ? { state: 'loading' }
      : WES_READY;
export const WES_NO_DATA_MESSAGE = 'No data for the current filters';
const DEFAULT_ERROR_MESSAGE = 'Unable to load this section.';

type WesSkeletonKind = 'cards' | 'chart' | 'table';

interface WesSectionStateProps {
  status?: WesSectionStatus;
  skeleton: WesSkeletonKind;
  // True when the section is ready but has nothing to show.
  isEmpty?: boolean;
  // Overrides the default empty copy where a section already has its own wording.
  emptyMessage?: string;
  testID: string;
  children: React.ReactNode;
}

const WesSectionState: React.FC<WesSectionStateProps> = ({
  status = WES_READY,
  skeleton,
  isEmpty = false,
  emptyMessage = WES_NO_DATA_MESSAGE,
  testID,
  children,
}) => {
  if (status.state === 'loading') {
    return (
      <View testID={`${testID}-loading`}>
        {skeleton === 'table' ? (
          <TableSkeleton rows={6} />
        ) : skeleton === 'chart' ? (
          <ChartSkeleton height={180} />
        ) : (
          <SkeletonBlock style={styles.cardsSkeleton} />
        )}
      </View>
    );
  }
  if (status.state === 'error') {
    return (
      <View testID={`${testID}-error`}>
        <SectionRetry message={status.message ?? DEFAULT_ERROR_MESSAGE} onRetry={status.onRetry} />
      </View>
    );
  }
  if (isEmpty) {
    return (
      <Text style={styles.empty} testID={`${testID}-empty`}>{emptyMessage}</Text>
    );
  }
  return <>{children}</>;
};

const styles = StyleSheet.create({
  cardsSkeleton: { height: 96, borderRadius: 12 },
  empty: { paddingVertical: 16, fontSize: 13, fontFamily: FontFamily.regular, color: '#6B7280' },
});

export default WesSectionState;
